export default class AudioPlayer {
   private id!: string;
   private player!: HTMLAudioElement;
   private html!: HTMLElement;
   private playBtn: HTMLElement | null = null;
   private pauseBtn: HTMLElement | null = null;
   private muteBtn: HTMLElement | null = null;
   private soundBtn: HTMLElement | null = null;
   private audioTime: HTMLElement | null = null;
   private audio: HTMLAudioElement | null = null;
   private playlistItems: NodeListOf<HTMLElement> | null = null;
   private progress: HTMLInputElement | null = null;
   private buffer: HTMLInputElement | null = null;
   private playing: boolean = false;
   private muted: boolean = false;
   private duration: number = 0;
   private el: HTMLElement = null as any;
   private songDuration: string = "0:00";
   private songLength: number = 0;
   private songCurrentTime: string = "0:00";

   // Store references to event listeners so we can remove them in destroy()
   private onMetadataLoaded = () => this.setData();
   private onTimeUpdate = () => this.handleTimeUpdate();
   private onEnded = () => this.looper();
   private onProgressClick = (ev: MouseEvent) => this.seekClicked(ev);
   private onPlayClick = () => this.togglePlay();
   private onPauseClick = () => this.togglePlay();
   private onMuteClick = () => this.toggleSound();
   private onSoundClick = () => this.toggleSound();
   private onAudioLoop = (ev: Event) => {
      const customEvent = ev as CustomEvent<{ id: string }>;
      if (customEvent.detail.id === this.id) {
         this.setLoop();
      }
   };
   private onPlaylistItemClick = (ev: MouseEvent) => this.playPlaylistSong(ev);
   private $posthog: any;

   constructor(url: string, id: string, el: HTMLElement) {
      // If no element ID is provided, silently exit
      if (!id) return;
      this.id = id;

      // Create an actual audio element in memory
      this.player = new Audio(url);
      this.$posthog = useNuxtApp().$posthog;

      // Attempt to get the container element
      //   const el = document.getElementById(id);

      if (!el) return;
      this.el = el;

      // Query all relevant child elements
      this.playBtn = el.querySelector(".control-play");
      this.pauseBtn = el.querySelector(".control-pause");
      this.muteBtn = el.querySelector(".control-mute");
      this.soundBtn = el.querySelector(".control-sound");
      this.audioTime = el.querySelector(".audio-time");
      this.audio = el.querySelector("audio");
      this.playlistItems = el.querySelectorAll(
         ".playlist-audio-item"
      ) as NodeListOf<HTMLElement>;
      this.progress = el.querySelector(
         ".audio-progress-bar"
      ) as HTMLInputElement;
      this.buffer = el.querySelector(
         ".audio-progress-buffer"
      ) as HTMLInputElement;

      // Setup
      this.setupPlayerAttr();
      this.bindEvents();
   }

   private bindEvents(): void {
      // Player events
      this.player.addEventListener("loadedmetadata", this.onMetadataLoaded);
      this.player.addEventListener("timeupdate", this.onTimeUpdate);
      this.player.addEventListener("ended", this.onEnded);

      // Progress bar
      if (this.progress) {
         this.progress.addEventListener("click", this.onProgressClick);
      }

      // Playback / Pause buttons
      this.playBtn?.addEventListener("click", this.onPlayClick);
      this.pauseBtn?.addEventListener("click", this.onPauseClick);

      // Mute / Sound buttons
      this.muteBtn?.addEventListener("click", this.onMuteClick);
      this.soundBtn?.addEventListener("click", this.onSoundClick);

      // Listen for a custom “audio:loop” event on window
      window.addEventListener("audio:loop", this.onAudioLoop);

      // Playlist items click
      if (this.playlistItems) {
         Array.from(this.playlistItems).forEach((item) => {
            item.addEventListener("click", this.onPlaylistItemClick);
         });
      }
   }

   /**
    * Removes all event listeners to clean up
    * when this player is no longer needed.
    */
   public destroy(): void {
      this.player.pause();
      // Player events
      this.player.removeEventListener("loadedmetadata", this.onMetadataLoaded);
      this.player.removeEventListener("timeupdate", this.onTimeUpdate);
      this.player.removeEventListener("ended", this.onEnded);

      // Progress bar
      this.progress?.removeEventListener("click", this.onProgressClick);

      // Playback / Pause buttons
      this.playBtn?.removeEventListener("click", this.onPlayClick);
      this.pauseBtn?.removeEventListener("click", this.onPauseClick);

      // Mute / Sound buttons
      this.muteBtn?.removeEventListener("click", this.onMuteClick);
      this.soundBtn?.removeEventListener("click", this.onSoundClick);

      // Custom “audio:loop” event
      window.removeEventListener("audio:loop", this.onAudioLoop);

      // Playlist items
      if (this.playlistItems) {
         Array.from(this.playlistItems).forEach((item) => {
            item.removeEventListener("click", this.onPlaylistItemClick);
         });
      }
   }

   private playPlaylistSong(ev: MouseEvent) {
      const target = ev.target as HTMLElement;
      const src = target.dataset.src;
      const title = target.innerText;

      if (!src) return;
      if (this.playing) {
         this.togglePlay();
      }

      const titleEl =
         this.el.querySelector(".audio-title strong") ||
         this.el.querySelector(".audio-title h4");
      if (titleEl) {
         titleEl.textContent = title;
      }

      this.player.setAttribute("src", src);
      this.player.load();

      this.togglePlay();
   }

   private setupPlayerAttr(): void {
      if (this.audio) {
         if (this.audio.loop) {
            this.player.loop = true;
         }
         if (this.audio.autoplay) {
            this.player.autoplay = true;
         }
      }
   }

   private setData(): void {
      this.duration = 0;
      this.songDuration = "0:00";
      this.songCurrentTime = "0:00";
      this.songLength = this.player.duration; // total length in seconds
      this.songDuration = this.convertTime(this.songLength);

      if (this.audioTime) {
         this.audioTime.innerHTML = `${this.songCurrentTime}/${this.songDuration}`;
      }

      if (this.buffer) {
         this.buffer.setAttribute("max", String(this.songLength));
      }

      if (this.progress) {
         this.progress.setAttribute("max", String(this.songLength));
      }
   }

   private handleTimeUpdate(): void {
      this.songCurrentTime = this.convertTime(this.player.currentTime);
      if (this.progress) {
         this.progress.value = String(this.player.currentTime);
      }

      if (this.audioTime) {
         this.audioTime.innerHTML = `${this.songCurrentTime}/${this.songDuration}`;
      }

      if (this.player.buffered.length) {
         const progressVal = this.player.buffered.end(
            this.player.buffered.length - 1
         );
         if (this.buffer) {
            this.buffer.value = String(progressVal);
         }
      }
   }

   private convertTime(time: number): string {
      const minutes = Math.floor(time / 60);
      let seconds: string | number = Math.floor(time - minutes * 60);
      if (seconds < 10) {
         seconds = "0" + seconds;
      }
      return `${minutes}:${seconds}`;
   }

   private seekClicked(ev: MouseEvent): void {
      if (!this.progress) return;

      const rect = this.progress.getBoundingClientRect();
      const position = ev.pageX - rect.left;
      const delta = position / this.progress.offsetWidth;

      this.songProgress = this.songLength * delta;
      this.player.currentTime = this.songProgress;
      this.progress.value = String(this.songProgress);
   }
   private songProgress: number = 0;

   private togglePlay(): void {

      if (this.playing) {
         this.playBtn?.classList.remove("hidden");
         this.pauseBtn?.classList.add("hidden");
         this.player.pause();
      } else {
         this.playBtn?.classList.add("hidden");
         this.pauseBtn?.classList.remove("hidden");
         this.player.volume = 0.7;
         this.player.play().catch((err) => console.warn("audio play failed", err));
         this.trackEvent();

      }
      this.playing = !this.playing;
   }

   private toggleSound(): void {
      if (this.muted) {
         this.player.muted = false;
         this.soundBtn?.classList.remove("hidden");
         this.muteBtn?.classList.add("hidden");
      } else {
         this.player.muted = true;
         this.soundBtn?.classList.add("hidden");
         this.muteBtn?.classList.remove("hidden");
      }
      this.muted = !this.muted;
   }

   public pause(): void {
      if (this.playing) {
         this.playBtn?.classList.remove("hidden");
         this.pauseBtn?.classList.add("hidden");
         this.player.pause();
         this.playing = false;
      }
   }

   private setLoop(): void {
      this.player.loop = !this.player.loop;
      if (this.audio) {
         this.audio.loop = this.player.loop;
      }
   }

   private looper(): void {
      if (this.player.loop) {
         this.player.currentTime = 0;
      } else {
         this.playBtn?.classList.remove("hidden");
         this.pauseBtn?.classList.add("hidden");
         this.player.pause();
         this.playing = false;
      }
   }

   private trackEvent(): void {
      const titleEl =
         this.el.querySelector<HTMLElement>(".audio-title strong") ||
         this.el.querySelector<HTMLElement>(".audio-title h4");
      const title = titleEl?.innerText || "Untitled";
      

      if ((window as any).gtag) {
         (window as any).gtag("event", "Audio Play", {
            event_category: "Audio",
            event_label: title,
            value: 1,
         });
      }
      this?.$posthog()?.capture("audio_played", {
         audio_src: this.player.src,
         audio_title: title,
      });
   }
}
